import { inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'

import { catchError, map, of, switchMap, tap } from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { CopyPasteEntityConfirmationModalComponent } from '@flowx/designer/modules/data-model-builder/components/copy-paste-entity-confirmation-modal/copy-paste-entity-confirmation-modal.component'
import { DataTypeActionConfirmationModalComponent } from '@flowx/designer/modules/data-model-builder/components/data-type-action-confirmation-modal/data-type-action-confirmation-modal.component'
import { isHttpConflictStatusCode } from '@flowx/shared/utils'

import { DataModelTypeEntity } from '../../../modules/core/dictionary/data-model.dictionary'
import { IError } from '../../api/api-http.service'
import { ProjectDataModelApiService } from '../../api/project-data-model.api'
import { mapResource } from '../../entities/application.entity'
import {
  ExpandSystemEntity,
  ProjectDataTypeToUIEntity,
  SystemEntityToUIEntity,
  UIEntityToDataModelTypeEntity,
} from '../../entities/data-model.entity'
import { ClipboardService } from '../../services/clipboard.service'
import { ResourceUsagesService } from '../../services/resource-usages.service'
import { ToastMessageService } from '../../services/toast-message.service'
import { UrlParametersStoreService } from '../../services/url-parameters-store.service'
import { ProjectDataModelActions } from '../actions/project-data-model.actions'

export const loadProjectDataModel$ = createEffect(
  (
    actions$ = inject(Actions),
    projectDataModelApiService = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService),
    urlParametersStoreService = inject(UrlParametersStoreService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.loadProjectDataModel),
      switchMap(() =>
        projectDataModelApiService.getProjectDataModel().pipe(
          map((resp) => {
            const { manifestInfo, resourceContent } = resp
            return {
              dataTypes: resourceContent.content,
              systemEntities: resourceContent.systemEntities,
              manifestInfo,
            }
          }),
          map(({ dataTypes, systemEntities, manifestInfo }) => {
            const manifestMap = manifestInfo.reduce((acc, m) => {
              acc[m.resId] = {
                appId: m.appId,
                appName: m.appName,
                appVersionId: m.appVersionId,
              }
              return acc
            }, {})
            return ProjectDataModelActions.loadProjectDataModelSuccess({
              dataTypes: dataTypes.map((d) =>
                ProjectDataTypeToUIEntity(
                  d,
                  manifestMap[d.resourceId],
                  urlParametersStoreService.urlParameters.appId ===
                    manifestMap[d.resourceId].appId &&
                    urlParametersStoreService.urlParameters.appVersionId ===
                      manifestMap[d.resourceId].appVersionId
                )
              ),
              systemEntities: systemEntities.flatMap((d) =>
                ExpandSystemEntity(SystemEntityToUIEntity(d))
              ),
            })
          }),
          catchError(({ error }: { error: IError }) => {
            toastMessageService.showErrorMessage('Load project data model failed', error.title)
            return of(ProjectDataModelActions.loadProjectDataModelFailure())
          })
        )
      )
    ),
  { functional: true }
)

export const addDataType$ = createEffect(
  (
    actions$ = inject(Actions),
    projectDataModelApiService = inject(ProjectDataModelApiService),
    urlParametersStoreService = inject(UrlParametersStoreService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.addDataType),
      switchMap(({ dataType }) =>
        projectDataModelApiService.createDataType(dataType).pipe(
          map((resp) => {
            const dataType = mapResource<DataModelTypeEntity>(resp)
            return ProjectDataModelActions.addDataTypeSuccess({
              dataType: ProjectDataTypeToUIEntity(dataType, {
                appId: urlParametersStoreService.urlParameters.appId,
                appVersionId: urlParametersStoreService.urlParameters.appVersionId,
                appName: 'Local',
                resId: dataType.resourceId,
              }),
            })
          }),
          catchError(({ error }: { error: IError }) => {
            toastMessageService.showErrorMessage('Add data type failed', error.title)
            return of(ProjectDataModelActions.addDataTypeFailure())
          })
        )
      )
    ),
  { functional: true }
)

export const updateDataType$ = createEffect(
  (
    actions$ = inject(Actions),
    projectDataModelApiService = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.updateDataType),
      switchMap(({ dataType }) =>
        projectDataModelApiService.updateDataType(dataType).pipe(
          map((resp) => {
            toastMessageService.showSuccessMessage('Data type updated successfully')
            return ProjectDataModelActions.updateDataTypeSuccess({
              dataType,
              cloneDataTypeId: resp.newResourceId,
            })
          }),
          catchError(({ error }: { error: IError }) => {
            toastMessageService.showErrorMessage('Update data type failed', error.title)
            return of(ProjectDataModelActions.updateDataTypeFailure())
          })
        )
      )
    ),
  { functional: true }
)

export const startDeleteDataType$ = createEffect(
  (
    actions$ = inject(Actions),
    resourceUsageService = inject(ResourceUsagesService),
    modalService = inject(FlxModalService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.deleteDataTypeRequested),
      switchMap(({ dataType: dataTypeEntity }) => {
        const dataType = UIEntityToDataModelTypeEntity(dataTypeEntity)
        return resourceUsageService.getLibraryResourceUsages(dataType.resourceDefinitionId).pipe(
          switchMap((resourceUsages) =>
            modalService
              .show(
                DataTypeActionConfirmationModalComponent,
                {
                  closeOnBackdropClick: false,
                  backdropClass: 'overlay-backdrop',
                },
                {
                  dataType,
                  resourceUsages,
                }
              )
              .data$.pipe(
                map(() =>
                  ProjectDataModelActions.deleteDataType({ dataTypeId: dataType.resourceId })
                )
              )
          )
        )
      })
    ),
  { functional: true }
)

export const deleteDataType$ = createEffect(
  (
    actions$ = inject(Actions),
    projectDataModelApiService = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.deleteDataType),
      switchMap(({ dataTypeId }) =>
        projectDataModelApiService.deleteDataType(dataTypeId).pipe(
          tap(() => {
            toastMessageService.showSuccessMessage('Data type deleted successfully')
          }),
          map(() => ProjectDataModelActions.deleteDataTypeSuccess({ dataTypeId })),
          catchError(({ error }: { error: IError }) => {
            toastMessageService.showErrorMessage('Delete data type failed', error.title)
            return of(ProjectDataModelActions.deleteDataTypeFailure())
          })
        )
      )
    ),
  { functional: true }
)

export const addAttributeToDataType$ = createEffect(
  (
    actions$ = inject(Actions),
    projectDataModelApiService = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.addAttributeToDataType),
      switchMap(({ dataTypeId, attribute }) =>
        projectDataModelApiService.createAttribute({ resourceId: dataTypeId }, attribute).pipe(
          map((resp) =>
            ProjectDataModelActions.addAttributeToDataTypeSuccess({
              dataTypeId,
              attribute: mapResource(resp),
              cloneDataTypeId: resp.newResourceId,
            })
          ),
          catchError(() => {
            toastMessageService.showErrorMessage('Add attribute to data type failed')
            return of(ProjectDataModelActions.addAttributeToDataTypeFailure())
          })
        )
      )
    ),
  { functional: true }
)

export const addDataTypeAsNewAttributeReference$ = createEffect(
  (
    actions$ = inject(Actions),
    projectDataModelApiService = inject(ProjectDataModelApiService),
    urlParametersStoreService = inject(UrlParametersStoreService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.addDataTypeAsNewAttributeReference),
      switchMap(({ newDataType, dataTypeId, attribute }) =>
        projectDataModelApiService.createDataType(newDataType).pipe(
          switchMap((resp) => {
            const newDataType = mapResource<DataModelTypeEntity>(resp)
            const refAttribute = {
              ...attribute,
              reference: {
                targetResourceDefinitionId: newDataType.resourceDefinitionId,
                appId: urlParametersStoreService.urlParameters.appId,
              },
            }
            return projectDataModelApiService
              .createAttribute({ resourceId: dataTypeId }, refAttribute)
              .pipe(
                map((resp) =>
                  ProjectDataModelActions.addDataTypeAsNewAttributeReferenceSuccess({
                    newDataTypeForReference: ProjectDataTypeToUIEntity(newDataType, {
                      appId: urlParametersStoreService.urlParameters.appId,
                      appVersionId: urlParametersStoreService.urlParameters.appVersionId,
                      appName: 'Local',
                      resId: newDataType.resourceId,
                    }),
                    dataTypeId,
                    attribute: mapResource(resp),
                    cloneDataTypeId: resp.newResourceId,
                  })
                ),
                catchError(() => {
                  toastMessageService.showErrorMessage('Add attribute to data type failed')
                  // TODO: maybe delete the new data type created before the error
                  return of(ProjectDataModelActions.addDataTypeAsNewAttributeReferenceFailure())
                })
              )
          }),
          catchError(() => {
            toastMessageService.showErrorMessage('Add attribute to data type failed')
            return of(ProjectDataModelActions.addDataTypeAsNewAttributeReferenceFailure())
          })
        )
      )
    ),
  { functional: true }
)

export const startEditAttribute$ = createEffect(
  (
    actions$ = inject(Actions),
    resourceUsageService = inject(ResourceUsagesService),
    flxModalService = inject(FlxModalService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.editAttributeRequested),
      switchMap(({ dataType, attribute }) =>
        resourceUsageService.getLibraryResourceUsages(dataType.resourceDefinitionId).pipe(
          switchMap((resourceUsages) =>
            flxModalService
              .show(
                DataTypeActionConfirmationModalComponent,
                {
                  closeOnBackdropClick: false,
                  backdropClass: 'overlay-backdrop',
                },
                {
                  dataType,
                  resourceUsages,
                  attribute,
                  action: 'update',
                }
              )
              .data$.pipe(
                map(() =>
                  ProjectDataModelActions.editAttribute({
                    dataTypeId: dataType.identifier,
                    attribute,
                  })
                )
              )
          )
        )
      )
    ),
  {
    functional: true,
  }
)

export const editAttribute$ = createEffect(
  (
    actions$ = inject(Actions),
    dataModelBuilderApiService = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService),
    flxModalService = inject(FlxModalService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.editAttribute),
      switchMap(({ dataTypeId, attribute }) =>
        dataModelBuilderApiService
          .updateAttributeInProjectDataModelType(dataTypeId, attribute)
          .pipe(
            map((resp) =>
              ProjectDataModelActions.editAttributeSuccess({
                dataTypeId,
                attribute: mapResource(resp),
                cloneDataTypeId: resp.newResourceId,
              })
            ),
            tap(() => flxModalService.hide()),
            catchError(() => {
              toastMessageService.showErrorMessage('Edit project data model attribute failed')
              return of(
                ProjectDataModelActions.editAttributeFailure({
                  dataTypeId,
                  attributeId: attribute.id,
                })
              )
            })
          )
      )
    ),
  {
    functional: true,
  }
)

export const updateAttributeValidators$ = createEffect(
  (
    actions$ = inject(Actions),
    dataModelBuilderApiService = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.updateAttributeValidators),
      switchMap(({ dataTypeId, attribute, validators }) =>
        dataModelBuilderApiService
          .updateAttributeValidatorsInProjectDataModelType(
            dataTypeId,
            attribute.flowxUuid,
            validators
          )
          .pipe(
            map((resp) =>
              ProjectDataModelActions.updateAttributeValidatorsSuccess({
                dataTypeId,
                attributeId: attribute.id,
                validators,
                cloneDataTypeId: resp.newResourceId,
              })
            ),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage(
                'Update data model attribute validations failed',
                error?.title
              )
              return of(
                ProjectDataModelActions.updateAttributeValidatorsFailure({
                  dataTypeId,
                  attributeId: attribute.id,
                })
              )
            })
          )
      )
    ),
  {
    functional: true,
  }
)

export const startDeleteAttribute$ = createEffect(
  (
    actions$ = inject(Actions),
    resourceUsageService = inject(ResourceUsagesService),
    flxModalService = inject(FlxModalService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.deleteAttributeRequested),
      switchMap(({ dataType, attribute }) =>
        resourceUsageService.getLibraryResourceUsages(dataType.resourceDefinitionId).pipe(
          switchMap((resourceUsages) =>
            flxModalService
              .show(
                DataTypeActionConfirmationModalComponent,
                {
                  closeOnBackdropClick: false,
                  backdropClass: 'overlay-backdrop',
                },
                {
                  dataType,
                  resourceUsages,
                  attribute,
                }
              )
              .data$.pipe(
                map(() =>
                  ProjectDataModelActions.deleteAttribute({
                    dataTypeId: dataType.identifier,
                    attribute,
                  })
                )
              )
          )
        )
      )
    ),
  {
    functional: true,
  }
)

export const deleteAttribute$ = createEffect(
  (
    actions$ = inject(Actions),
    dataModelBuilderApiService = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService),
    flxModalService = inject(FlxModalService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.deleteAttribute),
      switchMap(({ dataTypeId, attribute }) =>
        dataModelBuilderApiService
          .deleteAttributeInProjectDataModelType(dataTypeId, attribute.flowxUuid)
          .pipe(
            tap(() => {
              flxModalService.hide()
              toastMessageService.showSuccessMessage('Attribute deleted successfully')
            }),
            map((resp) =>
              ProjectDataModelActions.deleteAttributeSuccess({
                dataTypeId,
                attribute,
                cloneDataTypeId: resp.newResourceId,
              })
            ),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage('Delete attribute failed', error.title)
              return of(
                ProjectDataModelActions.deleteAttributeFailure({
                  dataTypeId,
                  attributeId: attribute.id,
                })
              )
            })
          )
      )
    ),
  {
    functional: true,
  }
)

export const editDataTypeAsNewAttributeReference$ = createEffect(
  (
    actions$ = inject(Actions),
    projectDataModelApiService = inject(ProjectDataModelApiService),
    urlParametersStoreService = inject(UrlParametersStoreService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.addDataTypeAsExistingAttributeReference),
      switchMap(({ newDataType, dataTypeId, attribute }) =>
        projectDataModelApiService.createDataType(newDataType).pipe(
          switchMap((resp) => {
            const newDataType = mapResource<DataModelTypeEntity>(resp)
            const refAttribute = {
              ...attribute,
              reference: { targetResourceDefinitionId: newDataType.resourceDefinitionId },
            }
            return projectDataModelApiService
              .updateAttributeInProjectDataModelType(dataTypeId, refAttribute)
              .pipe(
                map((resp) =>
                  ProjectDataModelActions.addDataTypeAsExistingAttributeReferenceSuccess({
                    newDataTypeForReference: ProjectDataTypeToUIEntity(newDataType, {
                      appId: urlParametersStoreService.urlParameters.appId,
                      appVersionId: urlParametersStoreService.urlParameters.appVersionId,
                      appName: 'Local',
                      resId: newDataType.resourceId,
                    }),
                    dataTypeId,
                    attribute: mapResource(resp),
                    cloneDataTypeId: resp.newResourceId,
                  })
                ),
                catchError((e) => {
                  toastMessageService.showErrorMessage(
                    e.error?.title || 'Update attribute to data type failed'
                  )
                  // TODO: maybe delete the new data type created before the error
                  return of(
                    ProjectDataModelActions.addDataTypeAsExistingAttributeReferenceFailure()
                  )
                })
              )
          }),
          catchError((e) => {
            toastMessageService.showErrorMessage(
              e.error?.title || 'Update attribute to data type failed'
            )
            return of(ProjectDataModelActions.addDataTypeAsExistingAttributeReferenceFailure())
          })
        )
      )
    ),
  { functional: true }
)

export const bulkUpdateAttributes$ = createEffect(
  (
    actions$ = inject(Actions),
    dataModelBuilderApiService = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.bulkUpdateAttributes),
      switchMap(({ dataTypeId, attributeUpdates }) =>
        dataModelBuilderApiService.bulkUpdateAttributes(dataTypeId, attributeUpdates).pipe(
          map((resp) =>
            ProjectDataModelActions.bulkUpdateAttributesSuccess({
              dataTypeId,
              attributes: mapResource(resp),
              cloneDataTypeId: resp.newResourceId,
            })
          ),
          catchError(({ error }: { error: IError }) => {
            toastMessageService.showErrorMessage(
              'Bulk update data type attributes failed',
              error.title
            )
            return of(
              ProjectDataModelActions.bulkUpdateAttributesFailure({
                dataTypeId,
              })
            )
          })
        )
      )
    ),
  { functional: true }
)

export const startPasteEntities$ = createEffect(
  (
    actions$ = inject(Actions),
    toastMessageService = inject(ToastMessageService),
    clipboardService = inject(ClipboardService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.pasteEntitiesRequested),
      switchMap(() =>
        clipboardService.pasteFrom('RESOURCE_DATA_MODEL').pipe(
          map((pastedEntitiesStringified) => {
            try {
              const pastedEntities = JSON.parse(pastedEntitiesStringified)
              return ProjectDataModelActions.pasteEntities({
                pastedEntities,
                force: false,
              })
            } catch (error) {
              toastMessageService.showErrorMessage('Error pasting entities!')
              return ProjectDataModelActions.pasteEntitiesFailure()
            }
          })
        )
      )
    ),
  {
    functional: true,
  }
)

export const pasteEntities$ = createEffect(
  (
    actions$ = inject(Actions),
    dataModelBuilderApiService = inject(ProjectDataModelApiService),
    flxModalService = inject(FlxModalService),
    toastMessageService = inject(ToastMessageService),
    urlParametersStoreService = inject(UrlParametersStoreService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.pasteEntities),
      switchMap(({ pastedEntities, force }) =>
        dataModelBuilderApiService.pasteEntities(pastedEntities, force).pipe(
          map((entities) => mapResource(entities)),
          map((entities) =>
            ProjectDataModelActions.pasteEntitiesSuccess({
              pastedEntities: entities.map((e) =>
                ProjectDataTypeToUIEntity(e, {
                  appId: urlParametersStoreService.urlParameters.appId,
                  appVersionId: urlParametersStoreService.urlParameters.appVersionId,
                  appName: 'Local',
                  resId: e.resourceId,
                })
              ),
            })
          ),
          tap(() => toastMessageService.showSuccessMessage('Entities pasted from clipboard')),
          tap(() => flxModalService.hide()),
          catchError((error) => {
            if (isHttpConflictStatusCode(error?.status)) {
              return flxModalService
                .show(
                  CopyPasteEntityConfirmationModalComponent,
                  { closeOnBackdropClick: true, backdropClass: 'overlay-backdrop' },
                  {
                    sourceEntities: pastedEntities.dataEntities.map((entity) => entity.name),
                    conflictedEntities: error.error?.namesThatClash,
                  }
                )
                .data$.pipe(
                  map(() =>
                    ProjectDataModelActions.pasteEntities({
                      pastedEntities,
                      force: true,
                    })
                  )
                )
            } else {
              toastMessageService.showErrorMessage('Paste entities failed')
              return of(ProjectDataModelActions.pasteEntitiesFailure())
            }
          })
        )
      )
    ),
  {
    functional: true,
  }
)
